import React, { useEffect, useState } from 'react';
import { Card, CardFooter, CardBody, Row, Col, Form } from 'reactstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { parse, endOfDay } from 'date-fns';
import { Link, useHistory, useParams } from 'react-router-dom';
import { Button, Field, FormAlerts, useSession } from '@cordata/buffet';
import PropTypes from 'prop-types';
import { orderBy, lowerCase } from 'lodash';

import { authorizedInstance as axios } from '~/utils';

const validationSchema = Yup.object().shape({
  note: Yup.string().required(),
  reminderDate: Yup.date().required(),
  // target: Yup.mixed().required(),
});

const initialValues = {
  reminderDate: '',
  note: '',
  target: '',
};

const RemindersForm = ({
  data,
  inline,
  setIsAddReminderFormOpen,
  setDidSaveReminder,
}) => {
  const { currentAppUser } = useSession();
  const history = useHistory();
  const params = useParams();
  const [providers, setProviders] = useState([]);

  useEffect(() => {
    if (currentAppUser) {
      axios
        .get('providers/mv', {
          params: {
            limit: 9999,
            org_users_only: true,
            'organization-id': currentAppUser.relationships.organizationUsers[0].attributes.orgUid,
            role_type: 'Navigator',
          },
        })
        .then(r => r.data.data)
        .then(r => r.map(rr => ({ ...rr, orderBy: lowerCase(rr.attributes.displayName) })))
        .then(r => orderBy(r, ['orderBy']))
        .then(r => setProviders(r));
    }
  }, [currentAppUser]);

  return (
    <Formik
      displayName="RemindersForm"
      validateOnBlur={false}
      validateOnChange={false}

      initialValues={{
        ...initialValues,
        ...data,
      }}

      validationSchema={validationSchema}

      onSubmit={(values, { setErrors, setSubmitting }) => {
        const p = {
          data: {
            type: 'reminders',
            subject_uid: inline ? values.patient.id : params.patient_id,
            subject_type: 'patient',
            created_date: null,
            updated_date: null,
            reminder_date: endOfDay(parse(values.reminderDate)).toISOString(),
            is_active: true,
            is_urgent: false,
            is_read: false,
            is_saved: false,
            note: values.note,
            display_name: values.target && values.target.attributes.displayName,
            target_uid: values.target && values.target.attributes.userUid,
            reminder_type: 'Communication',
            event_type_uid: 'a17ab328-2503-469e-a7dd-63c5e2147386',
            event_type_name: null,
            ui_model_name: null,
            route: null,
            links: {
              patient: {
                linkage: {
                  type: 'patients',
                  id: inline ? values.patient.id : params.patient_id,
                },
              },
            },
          },
        };

        axios
          .post('reminders', p)
          .then(() => (inline ? setDidSaveReminder(true) : history.push(`/patients/${params.patient_id}/details`)))
          .catch(e => setErrors({ server: e.errors }))
          .then(() => setSubmitting(false));
      }}
    >
      {({
        handleSubmit,
        isSubmitting,
        submitCount,
        errors,
      }) => (
        <Card>
          <Form onSubmit={handleSubmit} noValidate>
            <CardBody>
              { submitCount >= 1 && errors && <FormAlerts errors={errors} /> }

              {inline &&
                <Row form>
                  <Col>
                    <Field
                      name="patient"
                      label="Patient"
                      type="select"
                      required
                      noOptionsMessage={i => (!i.inputValue.length ? 'Search for patients ...' : 'No options')}
                      async
                      loadOptions={input =>
                        axios
                          .get(`/summary-patients?q=${input}`)
                          .then(r => r.data.data)
                          .then(r => r.map(rr => ({
                            label: `${rr.attributes.firstName} ${rr.attributes.lastName}`,
                            id: rr.id,
                          })))
                      }
                    />
                  </Col>
                </Row>
              }

              <Row form>
                <Col md={6}>
                  <Field
                    name="reminderDate"
                    label="Date"
                    type="date"
                    required
                  />
                </Col>

                <Col md={6}>
                  <Field
                    name="target"
                    label="Assigned To"
                    placeholder="Enter your assignee"
                    type="select"
                    options={providers}
                    getOptionValue={o => o.attributes.providerUid}
                    getOptionLabel={o => o.attributes.displayName}
                  />
                </Col>
              </Row>

              <Row form>
                <Col>
                  <Field
                    name="note"
                    type="textarea"
                    required
                  />
                </Col>
              </Row>
            </CardBody>

            <CardFooter className="d-flex">
              <div className="ml-auto">
                {inline ? (
                  <Button
                    type="cancel"
                    disabled={isSubmitting}
                    onClick={() => setIsAddReminderFormOpen(false)}
                  >
                    Cancel
                  </Button>
                ) : (
                  <Button
                    type="cancel"
                    disabled={isSubmitting}
                    tag={Link}
                    to={`/patients/${params.patient_id}/details`}
                  >
                    Cancel
                  </Button>
                )}

                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Working ...' : 'Save'}
                </Button>
              </div>
            </CardFooter>
          </Form>
        </Card>
      )}
    </Formik>
  );
};

RemindersForm.propTypes = {
  data: PropTypes.shape().isRequired,
  inline: PropTypes.bool.isRequired,
  setIsAddReminderFormOpen: PropTypes.func,
  setDidSaveReminder: PropTypes.func,
};

RemindersForm.defaultProps = {
  setIsAddReminderFormOpen: () => {},
  setDidSaveReminder: () => {},
};

export default RemindersForm;
